import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { BrandMark } from "@/components/Brand";
import MobileTabBar from "@/components/layout/MobileTabBar";
import LandlordPaywallGate from "@/components/LandlordPaywallGate";
// LLMStatusBanner import removed (Feb 2026) — banner mount deleted below.
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  LayoutDashboard, Building2, FileText, Wrench, CreditCard,
  ClipboardList, Receipt, Settings as SettingsIcon, LogOut,
  Leaf, Menu, X, Shield, BadgeCheck, KeyRound, Users2,
  Hammer, ScrollText, Video, Sparkles, MessageCircle, TrendingUp,
  BarChart3, ChevronDown, ChevronRight, FolderOpen, Calculator, Star,
  ShieldCheck, Zap, Cpu, Wallet, Gift, BookOpen, Heart, Mail, Trophy, Plug,
  Bot, Upload, Car, LifeBuoy, Handshake, Briefcase, Landmark, Megaphone, Target, Wand2,
  MessageSquareText, Gavel
} from "lucide-react";
import { useState, useRef, useEffect } from "react";

const LOGO_BLOCK_REMOVED = true;

const landlordSections = [
  {
    labelKey: "nav.section.overview", labelFallback: "Overview",
    items: [
      { to: "/dashboard", labelKey: "nav.dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/pm-workspace", labelKey: "nav.pm_workspace", label: "PM workspace", icon: Briefcase, testid: "nav-pm-workspace" },
      { to: "/funds", labelKey: "nav.funds", label: "Funds", icon: Landmark, testid: "nav-funds" },
      { to: "/briefing", labelKey: "nav.briefing", label: "Daily briefing", icon: BookOpen, testid: "nav-briefing" },
      { to: "/activity", labelKey: "nav.activity", label: "Operator activity", icon: Zap, testid: "nav-activity" },
      { to: "/reports", labelKey: "nav.reports", label: "Reports & tax", icon: BarChart3, testid: "nav-reports" },
    ],
  },
  {
    labelKey: "nav.section.portfolio", labelFallback: "Portfolio",
    items: [
      { to: "/properties", labelKey: "nav.properties", label: "Properties", icon: Building2, testid: "nav-properties" },
      { to: "/leases", labelKey: "nav.leases", label: "Leases", icon: FileText, testid: "nav-leases" },
      { to: "/landlord/setup", labelKey: "nav.quick_setup", label: "60-second setup", icon: Sparkles, testid: "nav-quick-setup" },
      { to: "/landlord/leads", labelKey: "nav.leads_inbox", label: "Rental leads inbox", icon: MessageCircle, testid: "nav-leads-inbox" },
      { to: "/renewals/batch", labelKey: "nav.batch_renewer", label: "Bulk rent-increase renewer", icon: TrendingUp, testid: "nav-batch-renewer" },
      { to: "/retention", labelKey: "nav.retention", label: "Long-term tenants", icon: Heart, testid: "nav-retention" },
      { to: "/applications", labelKey: "nav.applications", label: "Applications", icon: ClipboardList, testid: "nav-applications" },
      { to: "/tenant-placement", labelKey: "nav.tenant_placement", label: "Tenant placement", icon: Users2, testid: "nav-tenant-placement" },
      { to: "/tours", labelKey: "nav.tours", label: "Tours", icon: Video, testid: "nav-tours" },
      { to: "/parking", labelKey: "nav.parking", label: "Parking & storage", icon: Car, testid: "nav-parking" },
      { to: "/migration", labelKey: "nav.migration", label: "Portfolio migration", icon: Upload, testid: "nav-migration" },
      { to: "/migration/buildium/how-to", labelKey: "nav.migration_buildium", label: "How to move from Buildium", icon: FileText, testid: "nav-migration-buildium" },
    ],
  },
  {
    labelKey: "nav.section.operations", labelFallback: "Operations",
    items: [
      { to: "/tickets", labelKey: "nav.maintenance", label: "Maintenance", icon: Wrench, testid: "nav-tickets" },
      { to: "/work-orders", labelKey: "nav.work_orders", label: "Work orders", icon: Hammer, testid: "nav-work-orders" },
      { to: "/vendors", labelKey: "nav.vendors", label: "Vendors", icon: Users2, testid: "nav-vendors" },
      { to: "/landlord/concierge", labelKey: "nav.concierge", label: "Owner concierge", icon: Handshake, testid: "nav-concierge" },
      { to: "/service-workspace", labelKey: "nav.service_workspace", label: "Partner workspace", icon: Briefcase, testid: "nav-service-workspace" },
      { to: "/inspections", labelKey: "nav.inspections", label: "Inspections", icon: BadgeCheck, testid: "nav-inspections" },
      { to: "/access-keys", labelKey: "nav.access_keys", label: "Access keys", icon: KeyRound, testid: "nav-access-keys" },
    ],
  },
  {
    labelKey: "nav.section.finance", labelFallback: "Finance",
    items: [
      { to: "/payments", labelKey: "nav.payments", label: "Payments", icon: CreditCard, testid: "nav-payments" },
      { to: "/books", labelKey: "nav.books", label: "Books", icon: BookOpen, testid: "nav-books" },
      { to: "/bills", labelKey: "nav.bills", label: "Bills · AP", icon: Receipt, testid: "nav-bills" },
      { to: "/bank-recon", labelKey: "nav.bank_recon", label: "Bank reconciliation", icon: CreditCard, testid: "nav-bank-recon" },
      { to: "/settings/bank", labelKey: "nav.bank_link", label: "Link bank account", icon: Landmark, testid: "nav-bank-link" },
      { to: "/integrations", labelKey: "nav.integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
      { to: "/integrations/condoworks", labelKey: "nav.condoworks", label: "CondoWorks", icon: Building2, testid: "nav-condoworks" },
      { to: "/integrations/butterflymx", labelKey: "nav.butterflymx", label: "Butterfly MX", icon: KeyRound, testid: "nav-butterflymx" },
      { to: "/my-business", labelKey: "nav.my_business", label: "My Business", icon: TrendingUp, testid: "nav-my-business" },
      { to: "/revenue-engine", labelKey: "nav.revenue_engine", label: "Revenue Engine", icon: Sparkles, testid: "nav-revenue-engine" },
      { to: "/expenses", labelKey: "nav.expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses" },
      { to: "/utilities", labelKey: "nav.utilities", label: "Utilities", icon: Zap, testid: "nav-utilities" },
      { to: "/insurance", labelKey: "nav.insurance", label: "Insurance", icon: Shield, testid: "nav-insurance" },
      { to: "/notices", labelKey: "nav.notices", label: "Notices", icon: ScrollText, testid: "nav-notices" },
      { to: "/late-rent-autopilot", labelKey: "nav.autopilot", label: "Late-rent autopilot", icon: Zap, testid: "nav-autopilot" },
      { to: "/tenant-letters", labelKey: "nav.tenant_letters", label: "Tenant letters", icon: Mail, testid: "nav-tenant-letters" },
      { to: "/discounts", labelKey: "nav.discounts", label: "Auto discounts", icon: Sparkles, testid: "nav-discounts" },
      { to: "/automations", labelKey: "nav.automations", label: "Smart Automations", icon: Sparkles, testid: "nav-automations" },
      { to: "/automations/pricing", labelKey: "nav.dynamic_pricing", label: "Dynamic pricing", icon: TrendingUp, testid: "nav-pricing" },
      { to: "/automations/expenses", labelKey: "nav.expense_forecast", label: "Expense forecast", icon: BarChart3, testid: "nav-expense-forecast" },
      { to: "/marketing", labelKey: "nav.marketing", label: "Marketing Hub", icon: Megaphone, testid: "nav-marketing" },
      { to: "/leasing-assistant", labelKey: "nav.leasing_assistant", label: "Leasing Assistant", icon: MessageSquareText, testid: "nav-leasing-assistant" },
      { to: "/rubs", labelKey: "nav.rubs", label: "RUBS · utility split", icon: Calculator, testid: "nav-rubs" },
      { to: "/eviction", labelKey: "nav.eviction", label: "Eviction workflow", icon: Gavel, testid: "nav-eviction" },
      { to: "/key-wall", labelKey: "nav.key_wall", label: "Key wall", icon: KeyRound, testid: "nav-key-wall" },
      { to: "/gov", labelKey: "nav.gov", label: "Gov · Public housing", icon: Landmark, testid: "nav-gov" },
      { to: "/developer", labelKey: "nav.developer", label: "Developer API", icon: KeyRound, testid: "nav-developer" },
    ],
  },
  {
    labelKey: "nav.section.leads", labelFallback: "Lead generation",
    premiumOnly: true,
    items: [
      { to: "/tools/prospect-discovery", labelKey: "nav.prospect_discovery", label: "Find landlord & PM leads", icon: Target, testid: "nav-prospect-discovery" },
      { to: "/tools/ai-search-optimizer", labelKey: "nav.search_booster", label: "Search ranking booster", icon: Zap, testid: "nav-search-booster" },
      { to: "/tools/auto", labelKey: "nav.portfolio_copilot", label: "Portfolio copilot", icon: Wand2, testid: "nav-portfolio-copilot" },
      { to: "/revenue-optimizer", labelKey: "nav.revenue_optimizer", label: "Revenue Optimizer", icon: TrendingUp, testid: "nav-revenue-optimizer" },
    ],
  },
  {
    labelKey: "nav.section.smart", labelFallback: "Smart features",
    premiumOnly: true,
    items: [
      { to: "/smart-operator", labelKey: "nav.smart_operator", label: "Smart Operator", icon: Sparkles, testid: "nav-smart-operator", free: true },
      { to: "/copilot", labelKey: "nav.copilot", label: "Copilot hub", icon: Bot, testid: "nav-copilot" },
      { to: "/emails", labelKey: "nav.email_drafter", label: "Email drafter", icon: Mail, testid: "nav-email-drafter" },
      { to: "/resolutions", labelKey: "nav.resolutions", label: "Resolutions library", icon: LifeBuoy, testid: "nav-resolutions", free: true },
      { to: "/subleases", labelKey: "nav.subleases", label: "Sublease marketplace", icon: Handshake, testid: "nav-subleases", free: true },
      { to: "/screening", labelKey: "nav.screening", label: "Smart Screening", icon: ShieldCheck, testid: "nav-screening", free: true },
      { to: "/smart", labelKey: "nav.smart_features", label: "Smart suite", icon: Sparkles, testid: "nav-smart-features" },
      { to: "/messages", labelKey: "nav.messages", label: "Messages", icon: MessageCircle, testid: "nav-messages", free: true },
      { to: "/documents", labelKey: "nav.documents", label: "Documents", icon: FolderOpen, testid: "nav-documents", free: true },
      { to: "/compliance", labelKey: "nav.compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance" },
      { to: "/affordable-housing", labelKey: "nav.affordable_housing", label: "Affordable housing", icon: Heart, testid: "nav-affordable-housing" },
      { to: "/rent-analysis", labelKey: "nav.rent_analysis", label: "Rent analysis", icon: Sparkles, testid: "nav-rent-analysis" },
      { to: "/assistant", labelKey: "nav.assistant", label: "Ask Mark", icon: MessageCircle, testid: "nav-assistant" },
      { to: "/investor", labelKey: "nav.investor", label: "Investor portal", icon: TrendingUp, testid: "nav-investor" },
      { to: "/referrals", labelKey: "nav.referrals", label: "Referrals", icon: Gift, testid: "nav-referrals", free: true },
    ],
  },
  {
    labelKey: "nav.section.account", labelFallback: "Account",
    items: [
      { to: "/billing", labelKey: "nav.billing", label: "Billing", icon: Leaf, testid: "nav-billing" },
      { to: "/settings/automation", labelKey: "nav.automation_settings", label: "Automation settings", icon: Zap, testid: "nav-automation-settings" },
    ],
  },
];

const tenantSections = [
  {
    labelKey: "nav.section.home", labelFallback: "Home",
    items: [
      { to: "/dashboard", labelKey: "nav.overview_tenant", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/leases", labelKey: "nav.my_lease", label: "My lease", icon: FileText, testid: "nav-leases" },
      { to: "/tenant-community?tab=listings", labelKey: "nav.browse_rentals", label: "Browse rentals", icon: Building2, testid: "nav-tenant-rentals" },
      { to: "/subleases", labelKey: "nav.subleases", label: "Sublease marketplace", icon: Handshake, testid: "nav-tenant-subleases" },
    ],
  },
  {
    labelKey: "nav.section.living", labelFallback: "Living",
    items: [
      { to: "/payments", labelKey: "nav.pay_rent", label: "Pay rent", icon: CreditCard, testid: "nav-payments" },
      { to: "/tenant/rent", labelKey: "nav.rent_stripe", label: "Pay rent by card", icon: CreditCard, testid: "nav-tenant-rent-card" },
      { to: "/tenant/repairs", labelKey: "nav.repair_intake", label: "24/7 repair intake", icon: Wrench, testid: "nav-tenant-repairs" },
      { to: "/tickets", labelKey: "nav.maintenance", label: "Maintenance", icon: Wrench, testid: "nav-tickets" },
      { to: "/inspections", labelKey: "nav.inspections", label: "Inspections", icon: BadgeCheck, testid: "nav-inspections" },
      { to: "/smart-home", labelKey: "nav.smart_home", label: "Smart home", icon: Cpu, testid: "nav-smart-home" },
      { to: "/insurance", labelKey: "nav.renters_insurance", label: "Renter's insurance", icon: Shield, testid: "nav-insurance" },
    ],
  },
  {
    labelKey: "nav.section.money", labelFallback: "Money & credit",
    items: [
      { to: "/wellness", labelKey: "nav.financial_wellness", label: "Financial wellness", icon: Wallet, testid: "nav-wellness" },
      { to: "/rent-score", labelKey: "nav.rent_score", label: "Rent-on-time score", icon: Trophy, testid: "nav-rent-score" },
      { to: "/rent-calculator", labelKey: "nav.rent_calculator", label: "Rent calculator", icon: Calculator, testid: "nav-rent-calculator" },
      { to: "/documents", labelKey: "nav.my_documents", label: "My documents", icon: FolderOpen, testid: "nav-documents" },
      { to: "/settings/bank", labelKey: "nav.bank_link", label: "Link bank account", icon: Landmark, testid: "nav-tenant-bank-link" },
    ],
  },
  {
    labelKey: "nav.section.communication", labelFallback: "Communication",
    items: [
      { to: "/messages", labelKey: "nav.messages", label: "Messages", icon: MessageCircle, testid: "nav-messages" },
      { to: "/helpdesk", labelKey: "nav.tenant_helpdesk", label: "Help desk", icon: LifeBuoy, testid: "nav-tenant-helpdesk" },
      { to: "/resolutions", labelKey: "nav.tenant_resolutions", label: "Resolutions library", icon: BookOpen, testid: "nav-tenant-resolutions" },
      { to: "/tenant/resources", labelKey: "nav.tenant_resources", label: "Tenant resources hub", icon: BookOpen, testid: "nav-tenant-resources" },
      { to: "/assistant", labelKey: "nav.help_center", label: "Help center", icon: Sparkles, testid: "nav-assistant" },
      { to: "/review", labelKey: "nav.leave_review", label: "Leave a review", icon: Star, testid: "nav-review" },
      { to: "/referrals", labelKey: "nav.refer_friend", label: "Refer a friend", icon: Gift, testid: "nav-referrals" },
    ],
  },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const t = useT();
  const [open, setOpen] = useState(false);
  // Per-section collapsed state, persisted to localStorage so a
  // landlord's IA preference sticks across sessions. Default: only
  // the first section (Overview) is open on first visit — cuts the
  // sidebar from 60+ visible nav items down to 6 headers + Overview's
  // items. Sections containing the active route auto-expand.
  const [navExpanded, setNavExpanded] = useState(() => {
    try { return JSON.parse(localStorage.getItem("crc_nav_expanded") || "{}"); }
    catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem("crc_nav_expanded", JSON.stringify(navExpanded)); }
    catch { /* quota — harmless */ }
  }, [navExpanded]);
  const sections = user?.role === "tenant" ? tenantSections : landlordSections;
  // Feature-flag: hide Flinks/Funds nav until we go live in production.
  // Flip REACT_APP_FLINKS_VISIBLE=true in frontend/.env when ready.
  const flinksVisible = String(process.env.REACT_APP_FLINKS_VISIBLE || "").toLowerCase() === "true";
  const visibleSections = flinksVisible
    ? sections
    : sections.map((s) => ({
        ...s,
        items: s.items.filter((i) => i.to !== "/funds"),
      }));

  const handleLogout = async () => {
    // Capture role BEFORE logout clears user state, and remember it
    // so that role-aware redirects work even if React rerenders first.
    const portal = user?.role === "tenant" ? "tenant" : "landlord";
    try { localStorage.setItem("crc_last_portal", portal); } catch { /* storage quota */ }
    nav(`/login/${portal}`, { replace: true });
    // Logout AFTER navigation so the Protected wrapper doesn't override us.
    try { await logout(); } catch { /* best-effort */ }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* LLMStatusBanner intentionally NOT rendered (Feb 2026): user
          requested the offline-mode notice be removed. Every AI feature
          silently falls back to templates when the wallet is muted —
          landlords no longer see a yellow banner about it. */}
      <div className="flex-1 flex">
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-slate-200 px-4 h-16 flex items-center justify-between shadow-sm" data-testid="mobile-topbar">
        <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="mobile-logo">
          <BrandMark size={40} float/>
          <div className="leading-tight">
            <div className="brand-wordmark-bold text-slate-900">Central Rentals</div>
            <div className="brand-wordmark-sub text-emerald-700">Canada</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <PortalMoreMenu sections={sections} role={user?.role}/>
          <Link to="/settings" data-testid="mobile-account-link" aria-label={t("nav.settings", "Settings")}
            className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-semibold uppercase shadow-sm">
            {(user?.name || user?.email || "?").slice(0,1)}
          </Link>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="hamburger-card"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
          </button>
        </div>
      </div>

      <aside
        data-testid="app-sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 left-0 z-50 h-screen w-72 bg-white border-r border-slate-200 flex flex-col transition-transform`}
      >
        <div className="px-6 h-16 flex items-center gap-2 border-b border-slate-200 flex-none">
          <Link to="/dashboard" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
            <BrandMark size={40} float/>
            <div className="leading-tight">
              <div className="font-display font-bold text-[15px] text-slate-900">Central Rentals</div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-emerald-600 font-semibold">Canada</div>
            </div>
          </Link>
        </div>
        <div className="px-6 pt-3 flex-none" data-testid="appshell-sidebar-gov">
          {/* Government registered badge removed per product direction. */}
        </div>

        {user?.role && (
          <div className="px-4 pt-3 flex-none">
            <div
              data-testid={`role-pill-${user.role}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.16em] border
                ${user.role === "landlord" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : user.role === "tenant" ? "bg-sky-50 text-sky-700 border-sky-200"
                : "bg-amber-50 text-amber-700 border-amber-200"}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${user.role === "landlord" ? "bg-emerald-500" : user.role === "tenant" ? "bg-sky-500" : "bg-amber-500"}`}/>
              {user.role === "landlord" ? t("portal.landlord", "Landlord portal")
                : user.role === "tenant" ? t("portal.tenant", "Tenant portal")
                : t("portal.admin", "Admin portal")}
            </div>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto py-3 px-3">
          {visibleSections.map((sec, secIdx) => {
            const isSubscribed = user?.subscription_status === "active" || user?.subscription_status === "trialing";
            const items = sec.premiumOnly && !isSubscribed
              ? sec.items.filter((it) => it.free)
              : sec.items;
            if (items.length === 0) return null;
            const secKey = sec.labelKey || sec.label || `sec-${secIdx}`;
            const activeInSection = items.some((it) => loc.pathname === it.to || loc.pathname.startsWith(it.to + "/"));
            const expanded = navExpanded[secKey] ?? (secIdx === 0 || activeInSection);
            return (
              <div key={secKey} className="mb-3">
                <button
                  type="button"
                  onClick={() => setNavExpanded((s) => ({ ...s, [secKey]: !expanded }))}
                  data-testid={`nav-section-${secKey}`}
                  aria-expanded={expanded}
                  className="w-full px-3 mb-1 flex items-center justify-between text-[10px] tracking-[0.18em] uppercase font-bold text-slate-500 hover:text-slate-800"
                >
                  <span>{t(sec.labelKey, sec.labelFallback || sec.label)}</span>
                  <span className="text-slate-400 font-normal normal-case tracking-normal flex items-center gap-1">
                    <span className="text-[10px]">{items.length}</span>
                    {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  </span>
                </button>
                {expanded && (
                  <div className="space-y-0.5">
                    {items.map((it) => {
                      const Icon = it.icon;
                      const active = loc.pathname === it.to || loc.pathname.startsWith(it.to + "/");
                      return (
                        <Link
                          key={it.to}
                          to={it.to}
                          onClick={() => setOpen(false)}
                          data-testid={it.testid}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors
                            ${active ? "bg-emerald-50 text-emerald-700 font-semibold ring-1 ring-emerald-100" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}
                        >
                          <Icon className="w-[16px] h-[16px]"/>
                          <span>{t(it.labelKey, it.label)}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-200 p-3 space-y-1 flex-none">
          <Link to="/settings" onClick={() => setOpen(false)} data-testid="nav-settings"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900">
            <SettingsIcon className="w-[16px] h-[16px]"/> {t("nav.settings", "Settings")}
          </Link>
          <div className="px-3 py-2 flex justify-start"><LanguageSwitcher/></div>
          <div className="px-3 py-2 flex items-center gap-3">
            <Link to="/settings" onClick={() => setOpen(false)} data-testid="nav-account"
              className="flex items-center gap-3 min-w-0 flex-1 rounded-lg -mx-1 px-1 hover:bg-slate-100">
            <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center text-sm font-semibold uppercase shadow-sm">
              {(user?.name || user?.email || "?").slice(0,1)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900 truncate">{user?.name || t("nav.user", "User")}</div>
              <div className="text-xs text-slate-500 truncate capitalize">
                {user?.role === "landlord" ? t("label.landlord", "Landlord")
                  : user?.role === "tenant" ? t("label.tenant", "Tenant")
                  : (user?.role || "—")}
              </div>
            </div>
            </Link>
            <button onClick={handleLogout} data-testid="logout-button" title={t("nav.logout", "Log out")}
              className="text-slate-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50">
              <LogOut className="w-4 h-4"/>
            </button>
          </div>
        </div>
      </aside>

      {open && <div onClick={() => setOpen(false)} className="lg:hidden fixed inset-0 bg-slate-900/40 z-40"/>}

      <main
        data-role={user?.role || "guest"}
        className={`flex-1 min-w-0 ${user?.role === "tenant" ? "dash-bg-tenant" : "dash-bg-landlord"} pt-14 lg:pt-0 min-h-screen`}
      >
        <div className="max-w-[1500px] mx-auto p-4 md:p-6 lg:p-8 pb-28 lg:pb-28 text-slate-900">
          <LandlordPaywallGate>{children}</LandlordPaywallGate>
        </div>
        <PoweredByFooter role={user?.role} planKey={user?.plan_key} />
      </main>
      </div>{/* /flex-1 flex */}
      {/* Fixed bottom tab bar on mobile — matches modern PWA UX */}
      {(user?.role === "tenant" || user?.role === "landlord") && (
        <MobileTabBar role={user.role} />
      )}
    </div>
  );
}

/**
 * PoweredByFooter — the PLG viral loop. Every tenant that logs into the
 * portal (and every free-tier landlord) sees a subtle "Powered by
 * Central Rentals Canada" strip with a "Get your own" CTA. Tenants who
 * later become landlords, and vendors invited into maintenance threads,
 * get repeated brand impressions at zero marketing cost.
 *
 * Rendered for:
 *   • every tenant session (tenants always see the branded strip);
 *   • landlords on the free-forever plan (their tenants/vendors already
 *     see the badge on outgoing docs; we keep the founder aware of it).
 * Hidden for paying landlords (they paid to remove the badge).
 */
function PoweredByFooter({ role, planKey }) {
  // The tenant portal used to show a "Powered by Central Rentals Canada"
  // strip for viral-loop reasons. Removed per landlord request — the
  // tenant experience should read as fully white-labelled to their
  // landlord. We still keep the strip visible on free-forever landlord
  // accounts so *they* see the badge and the upgrade CTA.
  const show = planKey === "free" && role !== "tenant";
  if (!show) return null;
  return (
    <div
      className="border-t border-slate-200 bg-white/70 backdrop-blur px-4 md:px-6 lg:px-8 py-3"
      data-testid="powered-by-strip"
    >
      <div className="max-w-[1500px] mx-auto flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
        <span>
          Powered by{" "}
          <a
            href="https://centralrentals.ca"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-emerald-700 hover:text-emerald-800 underline underline-offset-2"
            data-testid="powered-by-link"
          >
            Central Rentals Canada
          </a>{" "}
          — the Canadian property OS for landlords.
        </span>
        <a
          href="https://centralrentals.ca/signup?ref=powered_by"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition"
          data-testid="powered-by-cta"
        >
          Get yours — free
        </a>
      </div>
    </div>
  );
}

/**
 * PortalMoreMenu — compact "More options" dropdown surfaced on the
 * mobile topbar (and injectable anywhere). Two-tap navigation to any
 * section: tap the button → tap the destination.
 *
 * Uses a native <details> element so it works without a portal, without
 * event listeners, and auto-closes on outside click on iOS Safari (where
 * synthetic outside-click handlers are historically flaky).
 */
function PortalMoreMenu({ sections, role }) {
  // Flatten section items into a single list capped at 24 rows so the
  // dropdown scrolls instead of overflowing the viewport on small phones.
  const items = [];
  for (const s of sections || []) {
    for (const it of s.items || []) {
      items.push({ ...it, section: s.labelFallback || s.label || "" });
    }
    if (items.length >= 60) break;
  }
  // Ref to the <details> element so we can programmatically close the
  // menu the instant a section is picked. Without this, iOS Safari
  // leaves the menu open after the route transitions.
  const detailsRef = useRef(null);
  const closeMenu = () => {
    const el = detailsRef.current;
    if (el && el.open) el.open = false;
  };
  return (
    <details ref={detailsRef} className="relative" data-testid="portal-more-menu">
      <summary
        aria-label="More options"
        className="list-none inline-flex items-center gap-1 h-10 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs cursor-pointer select-none"
        data-testid="portal-more-menu-trigger"
      >
        More <ChevronDown className="w-3.5 h-3.5"/>
      </summary>
      <div
        className="absolute right-0 top-full mt-2 w-72 max-h-[70vh] overflow-y-auto rounded-2xl bg-white border border-slate-200 shadow-2xl z-50"
        data-testid="portal-more-menu-panel"
      >
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
            {role === "tenant" ? "Tenant portal" : role === "landlord" ? "Landlord portal" : "Portal"}
          </div>
          <div className="font-display font-extrabold text-slate-900 text-sm">All sections</div>
        </div>
        <div className="py-1">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.to}
                to={it.to}
                onClick={closeMenu}
                data-testid={`portal-more-${it.testid || it.label}`}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50 text-slate-800 text-sm"
              >
                {Icon && <Icon className="w-4 h-4 text-slate-400 flex-none"/>}
                <span className="flex-1 truncate">{it.label}</span>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider">{it.section}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </details>
  );
}
